import type { GtagEvent, Slot } from '../../types/assertive'
import useOutbrainAds from '../../composables/ad-ops/useOutbrainAds'
import debug from '../console'

const logger = debug.withTag('adFunctions')

export function stripAYCode(placement: string): string {
  // Strip away "__ayManagerEnv__XX..." suffix off the element ID to get the placement class
  return placement.replace(/__ayManagerEnv__.+$/, '')
}

export function trackAssertiveRender(placement: string, adUnitPath: string) {
  const { track } = useAnalytics()
  const eventData: NtpAdEvent = {
    // ntp_tile_position: props.tilePosition,
    ntp_ad_brand: 'programmatic',
    ntp_ad_provider: 'programmatic',
    ntp_creative_id: adUnitPath,
    ntp_placement_name: placement,
    ntp_ad_refresh: false,
  }
  track('NtpAd_Rendered', eventData)
}

/**
 * Handles rendering an Outbrain native ad as a fallback when an Adx slot is empty and records analytics for filled slots.
 *
 * If the placement element cannot be located the function logs an error and returns.
 * When the provided event indicates an empty Adx slot, the function destroys the Adx instance for that element and renders the Outbrain fallback into the placement.
 * If the slot is filled, the function emits the assertive-render analytics event.
 *
 * @param event - Gtag slot-render event whose `isEmpty` property indicates whether the Adx slot returned no creative and whose `slot` provides the slot's identifiers
 */
export function setupNativeAdAsFallback(event: GtagEvent) {
  const elementId = event.slot.getSlotElementId() // Element id for Adx script
  const placementElement = document.getElementById(elementId)?.parentElement
  const placementClass = stripAYCode(elementId)

  if (!placementElement) {
    logger.error('Element not found for native ad fallback', placementClass)
    return
  }

  if (event.isEmpty) {
    try {
      const { renderFallbackOutbrainAd } = useOutbrainAds()
      // tryUseNuxtApp() is used here because this function runs as a googletag
      // event callback, outside of Vue component setup context.
      const ayManagerEnv = tryUseNuxtApp()?.$ayManagerEnv ?? window.ayManagerEnv
      ayManagerEnv?.cmd.push(function () {
        ayManagerEnv.destroy([elementId], {
          useInstanceCodes: true,
        })
      })
      renderFallbackOutbrainAd(placementClass, placementElement)
      return
    } catch (err) {
      logger.error(
        '[Native-Fallback]: Error on Gtag slotRenderEnded listener for native ad fallback',
        err
      )
    }
  }

  trackAssertiveRender(placementClass, event.slot.getAdUnitPath())
}

export function getFallbackPlacementWidget(
  placement: string
): OutbrainProps | undefined {
  const placementConfigs = useAppConfig().adops?.placements

  if (!placementConfigs) {
    logger.error('No outbrain widgets found')
    return
  }

  if (!placementConfigs || !placementConfigs[placement]?.outbrain) {
    console.error('No outbrain widget found for: ', placement)
    return
  }

  // return regular placement name so its targeted for removal in classList
  return { ...placementConfigs[placement].outbrain, fallback: true, placement }
}

// This function is used to get the Outbrain widget ID for a specific placement.
// Used to set the dynamic price for the placement.
export function getOutbrainWidgetIdForPlacement(
  placement: string
): string | undefined {
  const widgets = useAppConfig().adops?.placements

  if (!widgets) {
    logger.error('No mapping for placement to outbrain widgets found.')
    return
  }

  if (!widgets[placement] || !widgets[placement].outbrain?.widgetId) {
    logger.error('No outbrain widget found for: ', placement)
    return
  }

  return widgets[placement].outbrain.widgetId
}

export function getIndexOfPlacement(
  placement: string,
  el: Element | null
): number {
  if (!el || !placement) {
    logger.error('Element is null for placement:', placement)
    return 0
  }

  const placementDivs = Array.from(
    document.querySelectorAll(`.${placement}`)
  ).filter((div) => {
    return div.hasAttribute('ad')
  })

  if (placementDivs.length > 1 && el) {
    const index = Array.from(placementDivs).findIndex((div) => div === el)
    if (index !== -1) {
      return index
    }
  }
  return 0
}

export function handleStickyAd(placementDiv: Element, timeout = 3000): void {
  if (!placementDiv) {
    logger.error('No element to handle sticky ad')
    return
  }

  placementDiv.classList.add('sticky')
  placementDiv.classList.add('top-0')
  placementDiv.classList.add('z-[1000]')

  // Release the ad `timeout` ms after it actually pins to the top of the
  // viewport (rect.top <= 0), not on mount — so every lazily-mounted ad (e.g.
  // each block in the article stream) gets its own 3s of stickiness rather than
  // sharing a timer that started before it scrolled into view. The listener
  // removes itself once the timer is armed so it can't accumulate.
  const release = () => {
    if (placementDiv.getBoundingClientRect().top > 0) {
      return
    }
    window.removeEventListener('scroll', release)
    setTimeout(() => {
      placementDiv.classList.remove('sticky')
      placementDiv.classList.remove('top-0')
      placementDiv.classList.remove('z-[1000]')
    }, timeout)
  }
  window.addEventListener('scroll', release)
}

/**
 * Calculate decimal tile position for feed placements: card.tile format
 * e.g., card 5 + tile 2 → 5.3 (tileIdx is 0-based, so (2+1)/10 = 0.3)
 * Returns null if attributes are missing or non-numeric.
 *
 * Note: This encoding supports up to 9 tiles per card (tileIdx 0-8).
 * If tileIdx >= 9, the decimal overflows into the next integer (e.g., card 2
 * tile 9 → 3.0, colliding with card 3 tile 0). A warning is logged in that case.
 */
export function calculateFeedPosition(
  cardIdxAttr: string | null,
  tileIdxAttr: string | null
): number | null {
  if (cardIdxAttr === null || tileIdxAttr === null) {
    return null
  }

  const cardIdx = parseInt(cardIdxAttr, 10)
  const tileIdx = parseInt(tileIdxAttr, 10)

  if (!Number.isFinite(cardIdx) || !Number.isFinite(tileIdx)) {
    console.debug(
      'Non-numeric card/tile attributes, skipping feed position calculation',
      { cardIdxAttr, tileIdxAttr }
    )
    return null
  }

  if (tileIdx > 8) {
    console.warn(
      'Feed card has more than 9 tiles, decimal position encoding would collide — returning null',
      { cardIdxAttr, tileIdxAttr }
    )
    return null
  }

  return cardIdx + (tileIdx + 1) / 10
}

export function updateSlotTargeting(adUnitInstanceCode: string, slot: Slot) {
  const placement = stripAYCode(adUnitInstanceCode)

  slot.updateTargetingFromMap({
    placement,
  })
}
